import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Mail, RefreshCw, Send } from 'lucide-react';
import type { SmtpSettings, TemplateSetting } from '@dru/types';
import { BRAND } from '@dru/types';
import {
  Alert, Button, Card, CardBody, CardHeader, Checkbox, ConfirmDialog, ErrorState, Field, SearchSelect, StatusBadge,
  Textarea, TextField, TextSkeleton,
} from '@dru/ui';
import { ApiError } from '@dru/api-client';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';

/**
 * The two cards of the admin Email page: the SMTP server, and the wording of
 * each message. Split out of Settings.tsx so the page reads as groups — how
 * the platform connects, how it signs in, who mail comes from, and what it says.
 *
 * Field guidance sits behind ⓘ (`hint`), never between a label and its input:
 * a caption there pushed one column of the grid below the other. The line
 * under a label is kept for live state only — "from the environment".
 */

/** A titled group inside a card: a heading, then its fields. */
function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-small font-semibold text-neutral-900">{title}</h3>
      {children}
    </section>
  );
}

/** Asks before the tab closes or reloads over unsaved edits. */
function useLeaveWarning(dirty: boolean) {
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
}

/* ─────────────────────────────── SMTP ─────────────────────────────── */

const TRUTHY = ['true', '1', 'yes', 'on', 'ssl', 'tls'];

/**
 * Whether the server connects with TLS, as the sender decides it
 * (PlatformSettingsService.smtp): a saved or environment value wins; with
 * none, port 465 means TLS on connect, anything else STARTTLS. Treating
 * "absent" as false here once saved `secure: false` over a working 465 setup.
 */
function effectiveSecure(d: SmtpSettings): boolean {
  const raw = (d.fields.secure.value ?? '').toLowerCase();
  if (raw) return TRUTHY.includes(raw);
  const port = Number(d.fields.port.value ?? 587);
  return port === 465;
}

type SmtpForm = { host: string; port: string; user: string; pass: string; fromName: string; fromEmail: string; replyTo: string };

function formFrom(d: SmtpSettings): SmtpForm {
  const f = d.fields;
  return {
    host: f.host.value ?? '', port: f.port.value ?? '', user: f.user.value ?? '', pass: '',
    fromName: f.fromName.value ?? '', fromEmail: f.fromEmail.value ?? '', replyTo: f.replyTo.value ?? '',
  };
}

export function SmtpCard() {
  const smtp = useQuery<SmtpSettings>(() => api.get('/admin/settings/smtp'), []);
  // What is stored, as last read from the server — the baseline for "unsaved".
  const [stored, setStored] = useState<SmtpSettings | null>(null);
  const [form, setForm] = useState<SmtpForm | null>(null);
  const [secure, setSecure] = useState(false);
  const [clearPassword, setClearPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<string | null>(null);
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [testing, setTesting] = useState(false);

  /** Fills the form from a server response — the first load, or what Save returned. */
  function adopt(d: SmtpSettings) {
    setStored(d);
    setForm(formFrom(d));
    setSecure(effectiveSecure(d));
    setClearPassword(false);
  }

  // Only the first load fills the form; after that the Save response does, so
  // a stale query result can never overwrite what was just saved.
  useEffect(() => { if (smtp.data && !stored) adopt(smtp.data); }, [smtp.data, stored]);

  const d = stored;
  const baseline = d ? formFrom(d) : null;
  const dirty = Boolean(form && baseline && d && (
    (Object.keys(baseline) as Array<keyof SmtpForm>).some((k) => form[k] !== baseline[k])
    || secure !== effectiveSecure(d) || clearPassword
  ));
  useLeaveWarning(dirty);

  const set = (k: keyof SmtpForm) => (v: string) => {
    setForm((x) => (x ? { ...x, [k]: v } : x));
    setFieldErrors((e) => { const { [k]: _, ...rest } = e; return rest; });
    setSaved(null);
  };

  async function save() {
    if (!form || !d) return;
    setBusy(true); setError(null); setSaved(null); setFieldErrors({});
    try {
      const fresh = await api.put<SmtpSettings>('/admin/settings/smtp', {
        host: form.host.trim() || null,
        port: form.port.trim() ? Number(form.port) : null,
        user: form.user.trim() || null,
        pass: clearPassword ? null : form.pass,
        // Sent only when changed from what the sender already uses, so saving
        // other fields never rewrites an inferred TLS setting.
        ...(secure !== effectiveSecure(d) ? { secure } : {}),
        fromName: form.fromName.trim() || null,
        fromEmail: form.fromEmail.trim() || null,
        replyTo: form.replyTo.trim() || null,
      });
      adopt(fresh);
      setSaved('Saved. Outgoing mail uses these settings from the next message.');
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  async function test() {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post<{ ok: boolean; via?: string; error?: string }>('/admin/settings/smtp/test', { to: testTo.trim() });
      setTestResult(r.ok
        ? { ok: true, text: `Sent to ${testTo.trim()} via ${r.via}.` }
        : { ok: false, text: r.error ?? 'The test send failed.' });
    } catch (e) { setTestResult({ ok: false, text: errorMessage(e) }); } finally { setTesting(false); }
  }

  if (smtp.error && !d) {
    return <Card><CardBody><ErrorState title="SMTP settings could not be loaded" detail={smtp.error} onRetry={smtp.reload} /></CardBody></Card>;
  }

  // Live state, so it stays under the label: where the value in force comes from.
  const envStatus = (k: keyof SmtpSettings['fields']) => d?.fields[k].fromEnv ? 'From the environment — a value here overrides it.' : undefined;
  const testAddressOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testTo.trim());
  // Why Send test is off, said beside it. The test uses the saved settings,
  // so it waits for a save rather than testing something nobody stored.
  const testBlocked = !d ? 'Loading…'
    : busy ? 'Saving…'
      : dirty ? 'Save first — the test uses the saved settings.'
        : !d.configured ? 'Save a host and a from address first.'
          : !testAddressOk ? 'Enter the address to send to.' : null;
  const f = form;

  return (
    <Card>
      <CardHeader
        title="Outgoing email (SMTP)"
        hint="Welcome emails, appointment notices and the rest of the outbox go through this server."
        actions={d && (
          <StatusBadge tone={d.configured ? 'covered' : 'overdue'}>
            {d.configured
              ? `Sending · ${d.source === 'ENVIRONMENT' ? 'from the environment' : d.source === 'MIXED' ? 'saved + environment' : 'saved here'}`
              : 'Not sending'}
          </StatusBadge>
        )}
      />
      <CardBody className="space-y-6">
        {!f || !d ? <TextSkeleton lines={6} /> : (
          <>
            {!d.configured && (
              <Alert tone="warning" title="Email is logged, not sent">
                Until a host and a from address are saved (or set as SMTP_HOST / SMTP_FROM in the
                environment), outgoing email is written to the server log and nothing reaches an inbox.
              </Alert>
            )}

            <Group title="Connection">
              <div className="grid items-start gap-3 sm:grid-cols-[1fr_9rem]">
                <TextField label="SMTP host" value={f.host} onChange={set('host')} mono error={fieldErrors.host}
                  status={envStatus('host')} placeholder="smtp.example.com" />
                <TextField label="Port" value={f.port} onChange={(v) => set('port')(v.replace(/\D/g, ''))} mono inputMode="numeric"
                  error={fieldErrors.port} status={envStatus('port')} placeholder={secure ? '465' : '587'}
                  hint="587 for STARTTLS (the usual), 465 for TLS on connect. Blank uses 587." />
              </div>
              <Checkbox
                label={<span className="text-small">Use TLS from the start <span className="text-neutral-500">(port 465). Off: STARTTLS, usually port 587.</span></span>}
                checked={secure} onChange={(e) => { setSecure(e.target.checked); setSaved(null); }}
              />
              {envStatus('secure') && <p className="-mt-1 text-caption text-neutral-500">TLS setting {envStatus('secure')!.charAt(0).toLowerCase() + envStatus('secure')!.slice(1)}</p>}
            </Group>

            <Group title="Login">
              <div className="grid items-start gap-3 sm:grid-cols-2">
                <TextField label="Username" value={f.user} onChange={set('user')} mono error={fieldErrors.user} status={envStatus('user')} />
                <div>
                  <TextField label="Password" value={f.pass} onChange={set('pass')} mono type="password" error={fieldErrors.pass}
                    placeholder={d.passwordSet ? '•••••••• (set — leave blank to keep)' : 'Not set'}
                    hint="Stored encrypted and never shown again. Leave blank to keep the one saved."
                    status={d.passwordFromEnv ? 'From the environment. Typing one here overrides it.' : undefined} />
                  {d.passwordSet && !d.passwordFromEnv && (
                    <Checkbox className="mt-1" label={<span className="text-caption">Clear the stored password</span>} checked={clearPassword} onChange={(e) => setClearPassword(e.target.checked)} />
                  )}
                </div>
              </div>
            </Group>

            <Group title="Sender">
              <div className="grid items-start gap-3 sm:grid-cols-2">
                <TextField label="From name" value={f.fromName} onChange={set('fromName')} error={fieldErrors.fromName} status={envStatus('fromName')} placeholder={BRAND.longName} />
                <TextField label="From email" value={f.fromEmail} onChange={set('fromEmail')} mono type="email" error={fieldErrors.fromEmail} status={envStatus('fromEmail')} placeholder="no-reply@drultimate.com" />
                <TextField label="Reply-to" value={f.replyTo} onChange={set('replyTo')} mono type="email" error={fieldErrors.replyTo}
                  status={envStatus('replyTo')} hint="Where a reply lands. Usually the support address." />
              </div>
            </Group>

            {error && <Alert tone="danger">{error}</Alert>}
            {saved && <Alert tone="success">{saved}</Alert>}
            <div className="flex flex-wrap items-center justify-end gap-3">
              {dirty && !busy && <span className="text-caption text-neutral-600">Unsaved changes</span>}
              <Button onClick={save} loading={busy} disabled={!dirty || testing}>Save SMTP settings</Button>
            </div>

            <div className="border-t border-neutral-100 pt-5">
              <Group title="Send a test message">
                <p className="-mt-1 text-small text-neutral-600">Sends through the saved settings, to check mail really goes out.</p>
                <div className="flex flex-wrap items-end gap-2">
                  <TextField label="Send to" value={testTo} onChange={setTestTo} mono type="email" placeholder="you@example.com" className="w-full sm:w-72" />
                  <Button variant="secondary" onClick={test} loading={testing} disabled={!!testBlocked} icon={<Send className="h-4 w-4" />}>
                    Send test
                  </Button>
                </div>
                {testBlocked && !testing && <p className="text-caption text-neutral-500">{testBlocked}</p>}
                {testResult && <Alert tone={testResult.ok ? 'success' : 'danger'}>{testResult.text}</Alert>}
              </Group>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

/* ───────────────────────────── Templates ───────────────────────────── */

/** The messages, as somebody choosing one would name them. */
const EVENT_LABEL: Record<string, string> = {
  'auth.otp': 'Sign-in code (employee app)',
  'auth.password-reset': 'Password reset link',
  'staff.invited': 'Staff invitation',
  'role.changed': 'Role changed (to Super Admins)',
  'company.registered': 'Company registration received',
  'company.approved': 'Company approved — welcome',
  'appointment.booked': 'Appointment booked',
  'appointment.reminder': 'Appointment reminder',
  'appointment.missed': 'Appointment missed',
  'appointment.cancelled': 'Appointment cancelled',
  'consent.pending': 'Consent needed',
  'result.ready': 'Results ready',
  'certificate.issued': 'Certificate issued',
  'certificate.issued-hr': 'Certificate issued (to HR)',
  'certificate.expiring': 'Certificate expiring',
  'certificate.revoked': 'Certificate revoked',
  'critical.alert': 'Critical value alert',
  'compliance.overdue': 'Examinations overdue',
};
export const eventLabel = (e: string) => EVENT_LABEL[e]
  ?? (e.replace(/[._-]/g, ' ').replace(/^./, (c) => c.toUpperCase()));

export function TemplatesCard() {
  const templates = useQuery<{ items: TemplateSetting[]; variables: string[] }>(() => api.get('/admin/settings/templates'), []);
  const [event, setEvent] = useState('company.approved');
  const [pendingEvent, setPendingEvent] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  // The preview, and the wording it was built from — so an edit marks it stale.
  const [preview, setPreview] = useState<{ subject: string; body: string; from: { subject: string; body: string } } | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [resetting, setResetting] = useState(false);
  const bodyWrap = useRef<HTMLDivElement | null>(null);

  const items = templates.data?.items ?? [];
  const customised = items.filter((x) => x.customised).length;
  const t = items.find((x) => x.event === event);

  useEffect(() => {
    if (!t || loadedFor === event) return;
    setSubject(t.override.subject ?? '');
    setBody(t.override.body ?? '');
    setPreview(null); setSaved(false); setError(null);
    setLoadedFor(event);
  }, [t, event, loadedFor]);

  const dirty = Boolean(t && loadedFor === event
    && (subject !== (t.override.subject ?? '') || body !== (t.override.body ?? '')));
  useLeaveWarning(dirty);
  const previewStale = Boolean(preview && (preview.from.subject !== subject || preview.from.body !== body));

  /** Switching messages replaces the boxes — asked first when they hold unsaved edits. */
  function choose(next: string) {
    if (next === event) return;
    if (dirty) { setPendingEvent(next); return; }
    setEvent(next);
  }

  /** Saves the wording. Throws on failure so the reset dialog can stay open with the reason. */
  async function persist(reset: boolean) {
    await api.put(`/admin/settings/templates/${event}`, reset ? { subject: null, body: null } : { subject: subject || null, body: body || null });
    if (reset) { setSubject(''); setBody(''); setPreview(null); }
    setSaved(true);
    // Re-read so `customised` and the stored override match; the boxes stay as saved.
    templates.reload();
  }

  async function save() {
    setSaving(true); setError(null); setSaved(false);
    try { await persist(false); } catch (e) { setError(errorMessage(e)); } finally { setSaving(false); }
  }

  async function doPreview() {
    setPreviewing(true); setError(null);
    const from = { subject, body };
    try {
      const r = await api.post<{ subject: string; body: string }>(`/admin/settings/templates/${event}/preview`, { subject: subject || null, body: body || null });
      setPreview({ ...r, from });
    } catch (e) { setError(errorMessage(e)); } finally { setPreviewing(false); }
  }

  /** Puts `{{name}}` where the cursor is in the body, so nobody types braces by hand. */
  function insertVariable(v: string) {
    const token = `{{${v}}}`;
    const el = bodyWrap.current?.querySelector('textarea') ?? null;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end)); setSaved(false);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  }

  const usingBuiltIn = !subject && !body;
  const shown = preview ?? t?.default;

  return (
    <Card>
      <CardHeader
        title="Message wording"
        hint="Override the subject or body of an email. Leave a box empty to keep the built-in text. Clinical terms are refused — these go over email."
        actions={templates.data && (
          <span className="text-small text-neutral-600">
            <strong className="font-semibold tabular-nums text-neutral-900">{customised}</strong> of {items.length} customised
          </span>
        )}
      />
      <CardBody className="space-y-4">
        {templates.error && !templates.data ? (
          <ErrorState title="Message templates could not be loaded" detail={templates.error} onRetry={templates.reload} />
        ) : !templates.data ? (
          <TextSkeleton lines={8} />
        ) : (
          <>
            {/* SearchSelect carries its own search box — the list is 18 messages. */}
            <SearchSelect label="Message" value={event} onChange={choose} searchPlaceholder="Search messages…"
              options={items.map((x) => ({ value: x.event, label: eventLabel(x.event), hint: x.customised ? 'Customised' : undefined }))} />
            {t && (
              <div className="grid gap-5 xl:grid-cols-2">
                <div className="min-w-0 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge tone={t.customised ? 'info' : 'neutral'}>{t.customised ? 'Customised' : 'Built-in wording'}</StatusBadge>
                    {dirty
                      ? <span className="text-caption font-medium text-dueSoon-600">Unsaved changes</span>
                      : <span className="text-caption text-neutral-600">
                          {usingBuiltIn ? 'The grey text is the built-in wording. Type to replace it.' : 'Empty boxes fall back to the built-in wording.'}
                        </span>}
                  </div>
                  <TextField label="Subject" value={subject} onChange={(v) => { setSubject(v); setSaved(false); }} placeholder={t.default.subject} />
                  <div ref={bodyWrap}>
                    <Field label="Body">
                      {(p) => (
                        <Textarea {...p} value={body} onChange={(e) => { setBody(e.target.value); setSaved(false); }}
                          rows={14} placeholder={t.default.body} className="text-small leading-relaxed" />
                      )}
                    </Field>
                  </div>
                  {(templates.data.variables ?? []).length > 0 && (
                    <div>
                      <p className="mb-1.5 text-caption text-neutral-600">Insert a detail filled in for each message:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {templates.data.variables.map((v) => (
                          <button key={v} type="button" onClick={() => insertVariable(v)}
                            className="rounded-badge border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 font-mono text-caption text-neutral-700 transition-colors duration-hover hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 max-md:min-h-8">
                            {`{{${v}}}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap justify-end gap-2 pt-1">
                    <Button variant="ghost" onClick={() => setResetting(true)} disabled={saving || !t.customised}>Reset to built-in</Button>
                    <Button variant="secondary" onClick={doPreview} loading={previewing} disabled={saving} icon={<Mail className="h-4 w-4" />}>Preview</Button>
                    <Button onClick={save} loading={saving} disabled={!dirty || previewing}>Save wording</Button>
                  </div>
                  {error && <Alert tone="danger">{error}</Alert>}
                  {saved && !dirty && <Alert tone="success">Saved. The next “{eventLabel(event)}” email uses this wording.</Alert>}
                </div>

                <section aria-label={preview ? 'Preview with sample values' : 'Built-in wording'}
                  className="min-w-0 self-start rounded-card border border-neutral-200 bg-neutral-50">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-4 py-2.5">
                    <h3 className="text-small font-semibold text-neutral-900">{preview ? 'Preview with sample values' : 'Built-in wording'}</h3>
                    {preview && (
                      <button type="button" onClick={() => setPreview(null)} className="text-caption font-medium text-primary-600 hover:text-primary-700">
                        Show built-in
                      </button>
                    )}
                  </div>
                  {previewStale && (
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 bg-dueSoon-100 px-4 py-2 text-caption text-dueSoon-600">
                      <span className="font-medium">Out of date — the wording changed since this preview.</span>
                      <button type="button" onClick={doPreview} disabled={previewing}
                        className="inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline disabled:opacity-60">
                        <RefreshCw className="h-3 w-3" aria-hidden /> Refresh preview
                      </button>
                    </div>
                  )}
                  {shown && (
                    <div className={previewStale ? 'px-4 py-3 opacity-60' : 'px-4 py-3'}>
                      <p className="text-small font-semibold text-neutral-900">{shown.subject}</p>
                      <pre className="mt-2 whitespace-pre-wrap font-sans text-small leading-relaxed text-neutral-700">{shown.body}</pre>
                    </div>
                  )}
                </section>
              </div>
            )}
          </>
        )}

        {pendingEvent && (
          <ConfirmDialog
            title="Discard your changes?"
            what={`The edits to “${eventLabel(event)}” are not saved. Opening “${eventLabel(pendingEvent)}” replaces them.`}
            confirmLabel="Discard and switch"
            tone="danger"
            onCancel={() => setPendingEvent(null)}
            onConfirm={() => { setEvent(pendingEvent); setPendingEvent(null); }}
          />
        )}
        {resetting && t && (
          <ConfirmDialog
            title={`Reset “${eventLabel(event)}”?`}
            what="The customised subject and body are removed, and the next message sent uses the built-in wording."
            irreversible="Your custom wording is not kept anywhere. Copy it first if you may want it back."
            confirmLabel="Reset to built-in"
            tone="danger"
            onCancel={() => setResetting(false)}
            // persist() throws on failure: ConfirmDialog then stays open and shows why.
            onConfirm={async () => { setError(null); await persist(true); setResetting(false); }}
          />
        )}
      </CardBody>
    </Card>
  );
}
